import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, AlertTriangle, 
  Star, Activity, BarChart3, Eye, Target 
} from 'lucide-react';
import type { Product } from '@shared/schema';
import { formatPrice, calculateSavingsPercentage } from '@/lib/utils';

interface ProductAnalyticsProps {
  products: Product[];
}

interface ProductInsight {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  type: 'warning' | 'success' | 'info' | 'neutral';
  products?: Product[];
  count?: number;
  value?: number;
}

export function ProductAnalytics({ products }: ProductAnalyticsProps) {
  const analytics = useMemo(() => {
    if (!products || products.length === 0) {
      return {
        insights: [],
        topProducts: [],
        categoryStats: {},
        priceStats: {}
      };
    }

    const activeProducts = products.filter(p => p.active);
    const inactiveProducts = products.filter(p => !p.active);

    // Price analysis
    const prices = activeProducts.map(p => p.ourPrice);
    const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);

    // Savings analysis
    const productsWithSavings = activeProducts.filter(p => p.retailPrice > p.ourPrice);
    const avgSavings = productsWithSavings.length > 0 
      ? productsWithSavings.reduce((sum, p) => sum + calculateSavingsPercentage(p.retailPrice, p.ourPrice), 0) / productsWithSavings.length
      : 0;

    // Category breakdown
    const categoryStats = activeProducts.reduce((acc, product) => {
      const category = product.category;
      if (!acc[category]) {
        acc[category] = {
          count: 0,
          totalValue: 0,
          avgPrice: 0,
          avgSavings: 0
        };
      }
      acc[category].count++;
      acc[category].totalValue += product.ourPrice;
      
      return acc;
    }, {} as Record<string, { count: number; totalValue: number; avgPrice: number; avgSavings: number }>);

    // Calculate averages for categories
    Object.keys(categoryStats).forEach(category => {
      const stats = categoryStats[category];
      stats.avgPrice = stats.totalValue / stats.count;
      
      const categoryProducts = activeProducts.filter(p => p.category === category);
      const categoryWithSavings = categoryProducts.filter(p => p.retailPrice > p.ourPrice);
      stats.avgSavings = categoryWithSavings.length > 0
        ? categoryWithSavings.reduce((sum, p) => sum + calculateSavingsPercentage(p.retailPrice, p.ourPrice), 0) / categoryWithSavings.length
        : 0;
    });

    // Generate insights
    const insights: ProductInsight[] = [];

    // High-value products
    const highValueProducts = activeProducts.filter(p => p.ourPrice > avgPrice * 2);
    if (highValueProducts.length > 0) {
      insights.push({
        id: 'high-value',
        title: 'High-Value Products',
        description: `${highValueProducts.length} products priced significantly above average`,
        icon: DollarSign,
        type: 'info',
        products: highValueProducts,
        count: highValueProducts.length
      });
    }

    // Low-margin products
    const lowMarginProducts = activeProducts.filter(p => {
      const savings = calculateSavingsPercentage(p.retailPrice, p.ourPrice);
      return savings < 5 && p.retailPrice > p.ourPrice;
    });
    if (lowMarginProducts.length > 0) {
      insights.push({
        id: 'low-margin',
        title: 'Low Margin Products',
        description: `${lowMarginProducts.length} products with less than 5% savings`,
        icon: AlertTriangle,
        type: 'warning',
        products: lowMarginProducts,
        count: lowMarginProducts.length
      });
    }

    // Inactive products
    if (inactiveProducts.length > 0) {
      insights.push({
        id: 'inactive',
        title: 'Inactive Products',
        description: `${inactiveProducts.length} products are currently inactive`,
        icon: Eye,
        type: 'warning',
        products: inactiveProducts,
        count: inactiveProducts.length
      });
    }

    // Price optimization opportunities
    const overPricedProducts = activeProducts.filter(p => p.ourPrice > p.retailPrice);
    if (overPricedProducts.length > 0) {
      insights.push({
        id: 'overpriced',
        title: 'Price Review Needed',
        description: `${overPricedProducts.length} products priced above retail`,
        icon: TrendingDown,
        type: 'warning',
        products: overPricedProducts,
        count: overPricedProducts.length
      });
    }

    // Best savings opportunities
    const bestSavingsProducts = productsWithSavings
      .sort((a, b) => calculateSavingsPercentage(b.retailPrice, b.ourPrice) - calculateSavingsPercentage(a.retailPrice, a.ourPrice))
      .slice(0, 5);

    if (bestSavingsProducts.length > 0) {
      insights.push({
        id: 'best-savings',
        title: 'Top Savings Products',
        description: `Products offering the best customer savings`,
        icon: Star,
        type: 'success',
        products: bestSavingsProducts,
        count: bestSavingsProducts.length
      });
    }

    return {
      insights,
      topProducts: bestSavingsProducts,
      categoryStats,
      priceStats: {
        avgPrice,
        maxPrice,
        minPrice,
        avgSavings,
        totalProducts: activeProducts.length,
        inactiveProducts: inactiveProducts.length
      }
    };
  }, [products]);

  const getInsightBadgeColor = (type: ProductInsight['type']) => {
    switch (type) {
      case 'warning': return 'bg-amber-100 text-amber-800';
      case 'success': return 'bg-green-100 text-green-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Price</p>
                <p className="text-lg font-bold">{formatPrice(analytics.priceStats.avgPrice || 0)}</p>
              </div>
              <DollarSign className="w-6 h-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Savings</p>
                <p className="text-lg font-bold">{(analytics.priceStats.avgSavings || 0).toFixed(1)}%</p>
              </div>
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Price Range</p>
                <p className="text-lg font-bold">
                  {formatPrice(analytics.priceStats.minPrice || 0)} - {formatPrice(analytics.priceStats.maxPrice || 0)}
                </p>
              </div>
              <BarChart3 className="w-6 h-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Categories</p>
                <p className="text-lg font-bold">{Object.keys(analytics.categoryStats).length}</p>
              </div>
              <Package className="w-6 h-6 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insights */}
      {analytics.insights.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Product Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.insights.map((insight) => {
                const IconComponent = insight.icon;
                return (
                  <div key={insight.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50">
                    <IconComponent className="w-5 h-5 mt-0.5 text-gray-600" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{insight.title}</h4>
                        <Badge className={getInsightBadgeColor(insight.type)}>
                          {insight.count || 0}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">{insight.description}</p>
                    </div>
                    <Button variant="outline" size="sm">
                      View
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Category Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(analytics.categoryStats)
              .sort(([,a], [,b]) => b.count - a.count)
              .map(([category, stats]) => (
                <div key={category} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium capitalize">
                      {category.replace('-', ' ')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {stats.count} products • Avg: {formatPrice(stats.avgPrice)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{stats.avgSavings.toFixed(1)}%</p>
                    <p className="text-sm text-gray-600">avg savings</p>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Performing Products */}
      {analytics.topProducts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              Top Savings Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.topProducts.map((product, index) => {
                const savings = calculateSavingsPercentage(product.retailPrice, product.ourPrice);
                return (
                  <div key={product.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary">#{index + 1}</Badge>
                      <div>
                        <h4 className="font-medium">{product.description}</h4>
                        <p className="text-sm text-gray-600">
                          {formatPrice(product.ourPrice)} (was {formatPrice(product.retailPrice)})
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{savings.toFixed(1)}%</p>
                      <p className="text-sm text-gray-600">savings</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}